import {DatePicker, Form, Select} from "antd";
import {phoneCountryCodes} from "../../../helpers/phoneCountryCodes.helper";
import React, {ReactElement, useEffect} from "react";
import styles from "./Register.module.scss";
import LoginFormItemInput from "../../../components/UI/LoginFormItemInput/LoginFormItemInput";
import LoginFormItemInputPassword from "../../../components/UI/LoginFormItemInputPassword/LoginFormItemInputPassword";
import {useTranslation} from "react-i18next";
import {IRegisterRequest, IUserProfile} from "../../../models/Auth";
import dayjs from "dayjs";
import {openMessage} from "../../../store/notification/notificationSlice";
import moment from "moment/moment";
import {AuthService} from "../../../services/auth.service";
import useAppDispatch from "../../../hooks/useAppDispatch";
import {useNavigate} from "react-router-dom";
import {IMultiGymInfo} from "../../../store/multiGyms/multiGymsSlice";

interface IRegisterFullForm {
    profile: IUserProfile | null;
    disabled: boolean;
    renderBottom: () => ReactElement;
    email: string;
    selectedGym: IMultiGymInfo;
}

interface IRegisterModel {
    confirmPassword: string;
    password: string;
    dateOfBirth: Date;
    email: string;
    firstname: string;
    gender: string;
    lastname: string;
    phone: string;
    phoneCountryCode: string;
}

const RegisterForm: React.FC<IRegisterFullForm> = ({profile, disabled, renderBottom, email, selectedGym}) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const configForDatePicker = {rules: [{type: 'object' as const, required: true, message: t("PleaseSelectTime")}]};
    const [form] = Form.useForm();

    const initialFormValues: any = {};
    if (profile) {
        initialFormValues.firstname = profile.firstname;
        initialFormValues.lastname = profile.lastname;
        initialFormValues.phone = profile.phone;
        initialFormValues.email = profile.email;
        initialFormValues.phoneCountryCode = profile.phoneCountryCode;
        initialFormValues.gender = profile.gender;
        initialFormValues.dateOfBirth = dayjs(profile.dateOfBirth);
        initialFormValues.password = "*********";
        initialFormValues.confirmPassword = "*********";
    } else {
        initialFormValues.phoneCountryCode = phoneCountryCodes.find((phoneCode) => phoneCode.dial_code === "+30")?.dial_code;
        initialFormValues.email = email;
    }

    useEffect(() => {
        form.setFieldsValue(initialFormValues);
    }, [profile, form]);

    const onFinish = async (values: IRegisterModel) => {
        if (!disabled) {
            try {
                if (values.password.length < 6) {
                    dispatch(openMessage({title: t("PasswordMustBeLonger"), type: "error"}))
                    return;
                }

                if (values.password !== values.confirmPassword) {
                    dispatch(openMessage({title: t("PasswordsMustMatch"), type: "error"}))
                    return;
                }

                const model: IRegisterRequest = {
                    acceptTerms: true,
                    dateOfBirth: moment(values.dateOfBirth).toISOString(),
                    email: values.email,
                    firstname: values.firstname,
                    gender: values.gender,
                    lastname: values.lastname,
                    otp: "",
                    password: values.password,
                    phone: values.phone,
                    phoneCountryCode: values.phoneCountryCode,
                    registerExistingAccountOption: "email",
                }

                const registerResponse = await AuthService.register(model, selectedGym.endpoint);

                if (registerResponse.status === 200) {
                    dispatch(openMessage({type: "success", title: t("AccountRegistrationWasSuccessful")}));
                    navigate("/auth/login")
                } else {
                    dispatch(openMessage({type: "error", title: t("FailedToRegister")}));
                }

            } catch (err: any) {
                if (err.response?.data === "User exists") {
                    dispatch(openMessage({
                        type: "error",
                        title: t("FailedToRegister"),
                        description: t("ExistEmail", {email: values.email})
                    }));
                } else {
                    dispatch(openMessage({type: "error", title: t("FailedToRegister")}));
                }
            }
        } else {
            try {
                const response = await AuthService.createSecondaryAccount({email}, selectedGym.endpoint);
                if (response.status === 200) {
                    dispatch(openMessage({type: "success", title: t("AccountRegistrationWasSuccessful")}));
                    navigate("/auth/login")
                } else {
                    dispatch(openMessage({type: "error", title: t("FailedToRegister")}));
                }
            } catch (err) {
                dispatch(openMessage({type: "error", title: t("FailedToRegister")}));
            }
        }

    }

    return (
        <div className={styles.wrapper}>
            <h2 className={styles.title}>{t("JoinUs")}!</h2>
            <Form
                form={form}
                className={styles.form}
                name="register"
                onFinish={onFinish}
            >
                <div className={styles.row}>
                    <LoginFormItemInput
                        name={"firstname"}
                        label={t("FirstName")}
                        rules={{required: true}}
                        disabled={disabled}
                    />
                    <LoginFormItemInput
                        name={"lastname"}
                        label={t("LastName")}
                        rules={{required: true}}
                        disabled={disabled}
                    />
                </div>
                <LoginFormItemInput
                    disabled={true}
                    name={"email"}
                    label={t("EmailAddress")}
                    rules={{required: true}}
                />

                <div className={styles.row}>
                    <LoginFormItemInput
                        name={"phone"}
                        label={t("Phone")}
                        rules={{required: true}}
                        disabled={disabled}
                    />

                    <Form.Item
                        className="login-form__item"
                        label={t("PhoneCountryCode")}
                        name="phoneCountryCode"
                        rules={[{required: true}]}
                    >
                        <Select className="select" disabled={disabled}>
                            {phoneCountryCodes.map((option) => (
                                <Select.Option key={option.dial_code + option.code} value={option.dial_code}>
                                    {option.dial_code} {option.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </div>

                <div className={styles.row}>
                    <Form.Item
                        className="login-form__item"
                        label={t("Gender")}
                        name="gender"
                        rules={[{required: true}]}
                    >
                        <Select className="select" placeholder={t("Select")} disabled={disabled}>
                            <Select.Option value="female">{t("Female")}</Select.Option>
                            <Select.Option value="male">{t("Male")}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        className="login-form__item"
                        name="dateOfBirth"
                        label={t("DateOfBirth")}
                        {...configForDatePicker}>
                        <DatePicker
                            className="date-picker"
                            placeholder="MM/DD/YYYY"
                            format="MM/DD/YYYY"
                            disabled={disabled}
                        />
                    </Form.Item>
                </div>

                <LoginFormItemInputPassword
                    name={"password"}
                    label={t("Password")}
                    rules={{required: true}}
                    disabled={disabled}
                />

                <LoginFormItemInputPassword
                    name={"confirmPassword"}
                    label={t("ConfirmPassword")}
                    rules={{required: true}}
                    disabled={disabled}
                />

                {renderBottom()}

            </Form>
        </div>
    )


}

export default RegisterForm;